import {t} from 'i18next';
import {
  GA_EVENT_NAMES,
  GA_INTERNAL_DOMAINS,
  GA_PREFIX,
} from '../constants/analyticsConstants';
import {
  GAClickInfo,
  GADataLayerEvent,
  GAErrorInfo,
  GAEvent,
  GAEventDetail,
  GAFiles,
  GAMember,
  GAPageInfo,
  GAProject,
  GAReportInfo,
  GAStrInfo,
  GASubpageInfo,
  GAUserInfo,
} from '../types/analyticsTypes';
import {UserRoleCode} from '../types/user-role-codes/roleCodes';

let projectResetDone = true;
declare global {
  interface Window {
    dataLayer: any[];
    stackedEventObject: any;
  }
}

interface PushEventParams {
  eventName: GAEvent;
  page?: GAPageInfo;
  subPage?: GASubpageInfo;
  user?: GAUserInfo;
  report?: GAReportInfo;
  click?: GAClickInfo;
  error?: GAErrorInfo;
  eventDetail?: GAEventDetail;
  pushOnStackOrder?: number;
  str?: GAStrInfo;
  file_extension?: string;
  member?: GAMember;
  files?: GAFiles;
  project?: GAProject;
  hasProjectData?: boolean;
}

function deepLowercase(obj: any): any {
  if (typeof obj === 'string') {
    return obj.toLowerCase();
  }

  if (Array.isArray(obj)) {
    return obj.map(deepLowercase);
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = deepLowercase(obj[key]);
      return acc;
    }, {} as any);
  }

  return obj;
}

function applyPrePushFlush(event: GADataLayerEvent) {
  const pushes: any[] = [];
  if (event.event == GA_EVENT_NAMES.PAGE_VIEW) {
    const pageName = event?.page?.name || '';
    const prevPageName = event?.page?.prev_page_name || '';

    const isReportsPage =
      pageName.includes('reports') ||
      pageName.includes('str') ||
      pageName.includes('search-term-reports');

    // --- Rule 1: Global cleanup ---
    if (!isReportsPage) {
      pushes.push({
        report: undefined,
        str: undefined,
        click: undefined,
        error: undefined,
      });
    }

    // --- Rule 2: customreports + coming from STR ---
    if (
      pageName.includes('customreports') &&
      prevPageName.includes('aurora:str')
    ) {
      pushes.push({
        str: undefined,
        click: undefined,
        error: undefined,
      });
    }
  }

  if (event.hasProjectData) {
    projectResetDone = false;
  } else if (!projectResetDone) {
    pushes.push({
      project: undefined,
    });
    projectResetDone = true;
  }

  return pushes;
}

function applyPostPushFlush(event: GADataLayerEvent) {
  const pushes: any[] = [];

  // --- Subpage flush ---
  const hasSubpage =
    event?.page && (event.page.subpage_name || event.page.subpage_type);

  if (hasSubpage) {
    pushes.push({
      page: {
        subpage_name: undefined,
        subpage_type: undefined,
      },
    });
  }

  const isLogout =
    event?.click?.name === deepLowercase(t('gaTracking.global.logout'));

  if (isLogout) {
    pushes.push({
      page: undefined,
      user: undefined,
      report: undefined,
      str: undefined,
    });
  }

  if (event?.click?.details) {
    pushes.push({
      click: {
        details: undefined,
      },
    });
  }

  if (event?.member) {
    pushes.push({
      member: undefined,
    });
  }

  return pushes;
}

function triggerDataLayerPush(event: GADataLayerEvent) {
  const normalizedEvent = deepLowercase(event);

  // --- PRE PUSH ---
  const preFlushEvents = applyPrePushFlush(normalizedEvent);
  preFlushEvents.forEach((e) => dataLayerPush(e, 'pre_flush'));

  // --- MAIN EVENT ---
  delete normalizedEvent.hasProjectData
  dataLayerPush(normalizedEvent, 'main');

  // --- POST PUSH ---
  const postFlushEvents = applyPostPushFlush(normalizedEvent);
  postFlushEvents.forEach((e) => dataLayerPush(e, 'post_flush'));

  // TODO: Will be removed in UAT and prod
}

function stringifyWithUndefined(obj: any) {
  return JSON.stringify(
    obj,
    (_, value) => (value === undefined ? '__undefined__' : value),
    4
  );
}

function dataLayerPush(event: GADataLayerEvent, type: string) {
  window.dataLayer.push(event);
  /*
  console.log(
    'Aurora_GA',
    type,
    stringifyWithUndefined({
      ...event,
      user: event.user ? {...event.user, id: '###'} : undefined,
    })
  );*/
}

export function pushToDataLayer(params: PushEventParams) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  if (!window.stackedEventObject) {
    window.stackedEventObject = {};
  }

  const dataLayerEvent: GADataLayerEvent = {
    event: params.eventName,
  };

  if (params.page) {
    dataLayerEvent.page = params.page;
  }
  if (params.user) {
    dataLayerEvent.user = params.user;
  }

  if (params.subPage) {
    dataLayerEvent.page = params.subPage;
  }

  if (params.click) {
    dataLayerEvent.click = params.click;
  }

  if (params.error) {
    dataLayerEvent.error = params.error;
  }

  if (params.report) {
    dataLayerEvent.report = params.report;
  }

  if (params.str) {
    dataLayerEvent.str = params.str;
  }

  if (params.member) {
    dataLayerEvent.member = params.member;
  }

  if (params.files) {
    dataLayerEvent.files = params.files;
  }

  if (params.project) {
    dataLayerEvent.project = params.project;
  }

  if (params.file_extension) {
    dataLayerEvent.file_extension = params.file_extension;
  }

  if (params.hasProjectData) {
    dataLayerEvent.hasProjectData = params.hasProjectData;
  }
  
  if (params.pushOnStackOrder) {
    window.stackedEventObject[params.pushOnStackOrder] = dataLayerEvent;
  } else {
    triggerDataLayerPush(dataLayerEvent);
    Object.keys(window.stackedEventObject).forEach((key) => {
      if (window.stackedEventObject[key]) {
        triggerDataLayerPush(window.stackedEventObject[key]);
        delete window.stackedEventObject[key];
      }
    });
  }
}

export const generatePageAndSection = (
  url: string
): {page: string; section: string} => {
  const parsed = new URL(url, window.location.origin);

  const segments = parsed.pathname
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean);

  const params = new URLSearchParams(parsed.search);

  const parts = [...segments];

  if (segments.length === 1 && params.toString()) {
    for (const value of params.values()) {
      if (value) parts.push(value);
    }
  }

  return {
    page: GA_PREFIX + ':' + parts.join(':'),
    section: segments[0] || '',
  };
};

export async function getGAUserDetailsFromEmail(email: string) {
  const encoder = new TextEncoder();
  const trimmedText = email.toLowerCase().trim();
  const data = encoder.encode(trimmedText);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  const hashHex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  const domain = trimmedText.split('@')[1];
  const category = GA_INTERNAL_DOMAINS.includes(domain?.toLowerCase())
    ? 'internal'
    : 'external';

  return {
    id: hashHex,
    company_id: domain,
    role: '',
    category,
  };
}

export async function getUserRoleForGA(userRoles: any[], clientId?: string) {
  if (!Array.isArray(userRoles)) return 'no-role';
  if (clientId) {
    userRoles = userRoles.filter(
      (roleObj) => roleObj?.client?.clientId == clientId
    );
  }
  switch (userRoles?.[0]?.role?.roleCode) {
    case UserRoleCode.systemAdminUserRole:
      return 'admin';
    case UserRoleCode.clientAdminUserRole:
      return 'client-admin';
    case UserRoleCode.consilioUserRole:
      return 'consilio-admin';
    case UserRoleCode.clientUserRole:
      return 'client-user';
    default:
      return 'no-role';
  }
}

export const isCurrentUrlMatch = (
  pathname: string,
  list: string[]
): boolean => {
  return list.some((url) => {
    if (url === '/') {
      return pathname === '/';
    }
    return pathname === url || pathname.startsWith(`${url}/`);
  });
};
